import './styles.css';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { isCompareGraphOverlay } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphToolbarButton } from 'components/Graph/GraphToolbar/GraphToolbarButton';
import { GraphToolbarCollapseMenu } from 'components/Graph/GraphToolbar/GraphToolbarCollapseMenu';
import { GraphToolbarHiddenNodesMenu } from 'components/Graph/GraphToolbar/GraphToolbarHiddenNodesMenu';
import { selectGraphToolbarDisabled } from 'components/Graph/GraphToolbar/selectors/selectGraphToolbarDisabled';
import { alignSelectedNodes } from 'components/Graph/commands/alignSelectedNodes';
import { distributeSelectedNodes } from 'components/Graph/commands/distributeSelectedNodes';
import { fitGraphToView } from 'components/Graph/commands/fitGraphToView';
import { redo } from 'components/Graph/commands/history/redo';
import { undo } from 'components/Graph/commands/history/undo';
import { debugPathlookup } from 'components/Graph/commands/pathLookup/debugPathlookup';
import { goToTechsupportFromPathlookup } from 'components/Graph/commands/pathLookup/goToTechsupportFromPathlookup';
import { recalculateLayout } from 'components/Graph/commands/recalculateLayout';
import { selectPathLookupApplied } from 'components/Graph/model/selectors/graphMetadata/selectPathLookupApplied';
import { ScrollingWrapper } from 'components/ScrollingWrapper/ScrollingWrapper';
import { AlignDownIcon } from 'components/icons/AlignDownIcon';
import { AlignLeftIcon } from 'components/icons/AlignLeftIcon';
import { AlignRightIcon } from 'components/icons/AlignRightIcon';
import { AlignUpIcon } from 'components/icons/AlignUpIcon';
import { DistributeHorizontalIcon } from 'components/icons/DistributeHorizontalIcon';
import { DistributeVerticalIcon } from 'components/icons/DistributeVerticalIcon';
import { RedoIcon } from 'components/icons/RedoIcon';
import { RefreshIcon } from 'components/icons/RefreshIcon';
import { UndoIcon } from 'components/icons/UndoIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { flow } from 'fp-ts/lib/function';
import type { TechsupportNavigationState } from 'pages/Support/Techsupport/Techsupport.utils';
import { memo } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { canRedo, canUndo } from 'utils/history/CallbackHistory';
import { useHasPermission } from 'utils/permissions/useHasPermission';
import { GraphApiDocumentation } from '../GraphApiDocumentation/GraphApiDocumentation';
import { CenterIcon } from '../GraphSidePanel/icons/CenterIcon';
import { GraphLoadView } from '../GraphView/GraphLoadView';
import { SaveView } from '../GraphView/SaveView';
import { SaveViewAs } from '../GraphView/SaveViewAs';
import { ShareView } from '../GraphView/ShareView';
import { GraphToolbarDebugPathlookupButton } from './GraphToolbarDebugPathlookupButton';
import { GraphToolbarExportMenu } from './GraphToolbarExportMenu';
import { GraphToolbarSearch } from './GraphToolbarSearch';
import { GraphToolbarSnapshotCreation } from './GraphToolbarSnapshotCreation';
import { GraphToolbarTechsupportButton } from './GraphToolbarTechsupportButton';

interface GraphToolbarTechsupportActionProps {
  disabled: boolean;
  dispatch: (
    command: typeof goToTechsupportFromPathlookup,
  ) => Promise<TechsupportNavigationState | null>;
  pathLookupApplied: boolean;
}

const GraphToolbarTechsupportAction: FC<GraphToolbarTechsupportActionProps> = ({
  disabled,
  dispatch,
  pathLookupApplied,
}) => {
  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  if (!hasPermission('settings')) {
    return null;
  }

  return (
    <GraphToolbarTechsupportButton
      disabled={!pathLookupApplied || disabled}
      onClick={async () => {
        const navState = await dispatch(goToTechsupportFromPathlookup);
        if (navState) {
          navigate('/support/techsupport', { state: navState });
        }
      }}
    />
  );
};

export const BareGraphToolbar: FC = () => {
  const {
    canvasGraph: {
      graphToolbar: { buttons: msgButtons },
    },
  } = useLocale();
  const ctxProps = useGraphContextSelectors({
    atLeast2NodesSelected: (s) => s.graph.selectedNodeIds.size >= 2,
    dispatch: prop('dispatch'),
    disabled: selectGraphToolbarDisabled,
    pathLookupApplied: selectPathLookupApplied,
    redoEnabled: flow(prop('history'), canRedo),
    saveViewDisabled: flow(prop('overlay'), isCompareGraphOverlay),
    sidePanelOpen: prop('sidePanel', 'open'),
    undoEnabled: flow(prop('history'), canUndo),
  });
  // CommandDispatcher's public type erases handler return values; this command returns navigation state at runtime.
  const dispatchTechsupport =
    ctxProps.dispatch as unknown as GraphToolbarTechsupportActionProps['dispatch'];

  return (
    <ScrollingWrapper arrowsWidth={40}>
      <div className='graph-toolbar'>
        <GraphToolbarSearch disabled={ctxProps.disabled} />
        <div className='graph-toolbar__separator' />
        <GraphLoadView />
        <div className='graph-toolbar__separator' />
        <SaveViewAs disabled={ctxProps.disabled} />
        <SaveView disabled={ctxProps.disabled} />
        <div className='graph-toolbar__separator' />
        <GraphToolbarSnapshotCreation disabled={ctxProps.disabled} />
        <div className='graph-toolbar__separator' />
        <ShareView disabled={ctxProps.disabled} />
        <div className='graph-toolbar__separator' />
        <GraphToolbarButton
          disabled={ctxProps.disabled}
          disabledTitle={msgButtons.selectDataToDisplay}
          onClick={() => {
            ctxProps.dispatch(fitGraphToView);
          }}
          title={msgButtons.fitView}
        >
          <CenterIcon title={msgButtons.fitView} width={16} height={16} />
        </GraphToolbarButton>
        <div className='graph-toolbar__separator' />
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.undoEnabled}
          disabledTitle={msgButtons.undoDisabled}
          onClick={() => {
            ctxProps.dispatch(undo);
          }}
          title={msgButtons.undo}
        >
          <UndoIcon title={msgButtons.undo} />
        </GraphToolbarButton>
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.redoEnabled}
          disabledTitle={msgButtons.redoDisabled}
          onClick={() => {
            ctxProps.dispatch(redo);
          }}
          title={msgButtons.redo}
        >
          <RedoIcon title={msgButtons.redo} />
        </GraphToolbarButton>
        <div className='graph-toolbar__separator' />
        <GraphToolbarButton
          disabled={ctxProps.disabled}
          disabledTitle={msgButtons.selectDataToDisplay}
          onClick={() => {
            ctxProps.dispatch(recalculateLayout);
          }}
          title={msgButtons.recalculateLayout}
        >
          <RefreshIcon
            title={msgButtons.recalculateLayout}
            width={16}
            height={16}
          />
        </GraphToolbarButton>
        <div className='graph-toolbar__separator' />
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.atLeast2NodesSelected}
          disabledTitle={
            ctxProps.disabled
              ? msgButtons.selectDataToDisplay
              : msgButtons.alignUpDisabled
          }
          onClick={() => {
            ctxProps.dispatch(alignSelectedNodes, { direction: 'up' as const });
          }}
          title={msgButtons.alignUp}
        >
          <AlignUpIcon title={msgButtons.alignUp} width={17} height={15} />
        </GraphToolbarButton>
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.atLeast2NodesSelected}
          disabledTitle={
            ctxProps.disabled
              ? msgButtons.selectDataToDisplay
              : msgButtons.alignDownDisabled
          }
          onClick={() => {
            ctxProps.dispatch(alignSelectedNodes, {
              direction: 'down' as const,
            });
          }}
          title={msgButtons.alignDown}
        >
          <AlignDownIcon title={msgButtons.alignDown} width={18} height={18} />
        </GraphToolbarButton>
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.atLeast2NodesSelected}
          disabledTitle={
            ctxProps.disabled
              ? msgButtons.selectDataToDisplay
              : msgButtons.alignLeftDisabled
          }
          onClick={() => {
            ctxProps.dispatch(alignSelectedNodes, {
              direction: 'left' as const,
            });
          }}
          title={msgButtons.alignLeft}
        >
          <AlignLeftIcon title='Align down' width={15} height={17} />
        </GraphToolbarButton>
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.atLeast2NodesSelected}
          disabledTitle={
            ctxProps.disabled
              ? msgButtons.selectDataToDisplay
              : msgButtons.alignRightDisabled
          }
          onClick={() => {
            ctxProps.dispatch(alignSelectedNodes, {
              direction: 'right' as const,
            });
          }}
          title={msgButtons.alignRight}
        >
          <AlignRightIcon
            title={msgButtons.alignRight}
            width={15}
            height={17}
          />
        </GraphToolbarButton>
        <div className='graph-toolbar__separator' />
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.atLeast2NodesSelected}
          disabledTitle={
            ctxProps.disabled
              ? msgButtons.selectDataToDisplay
              : msgButtons.distributeHorizontalDisabled
          }
          onClick={() => {
            ctxProps.dispatch(distributeSelectedNodes, { axis: 'x' } as const); // as const can be removed with TS 4.2+
          }}
          title={msgButtons.distributeHorizontal}
        >
          <DistributeHorizontalIcon
            title={msgButtons.distributeHorizontal}
            width={18}
            height={12}
          />
        </GraphToolbarButton>
        <GraphToolbarButton
          disabled={ctxProps.disabled || !ctxProps.atLeast2NodesSelected}
          disabledTitle={
            ctxProps.disabled
              ? msgButtons.selectDataToDisplay
              : msgButtons.distributeVerticalDisabled
          }
          onClick={() => {
            ctxProps.dispatch(distributeSelectedNodes, { axis: 'y' } as const); // as const can be removed with TS 4.2+
          }}
          title={msgButtons.distributeVertical}
        >
          <DistributeVerticalIcon
            title={msgButtons.distributeVertical}
            width={11}
            height={17}
          />
        </GraphToolbarButton>
        <div className='graph-toolbar__separator' />
        <GraphToolbarCollapseMenu />
        <div className='graph-toolbar__separator' />
        <GraphToolbarHiddenNodesMenu />
        <div className='graph-toolbar__separator' />
        <GraphToolbarExportMenu />
        <div className='graph-toolbar__separator' />
        <GraphApiDocumentation />
        <div className='graph-toolbar__separator' />
        <GraphToolbarTechsupportAction
          disabled={ctxProps.disabled}
          dispatch={dispatchTechsupport}
          pathLookupApplied={ctxProps.pathLookupApplied}
        />
        {isFeatureFlagEnabled('ENABLE_DEBUG_PATHLOOKUP') && (
          <GraphToolbarDebugPathlookupButton
            disabled={!ctxProps.pathLookupApplied}
            onClick={() => ctxProps.dispatch(debugPathlookup)}
          />
        )}
      </div>
    </ScrollingWrapper>
  );
};

export const GraphToolbar = memo(BareGraphToolbar);
